// Servicio centralizado para manejo inteligente de ubicaciones
import { findLocationByName, findLocationByCoordinatesLocal } from '../services/locationsApi';
import { geolocationService } from '../services/geolocationService';

export class SmartLocationService {

    // 🎯 Función principal para detectar ubicación usando GPS con nueva lógica
    static async detectLocationFromGPS(latitude, longitude, availableLocations) {
        try {
            // PASO 1: Consultar LocationIQ para obtener el nombre
            const locationIQResult = await geolocationService.reverseGeocode(latitude, longitude);

            if (locationIQResult) {
                // 🆕 Extraer el campo 'town' o usar display_name como fallback
                const locationName = this.extractLocationName(locationIQResult);

                // PASO 2: Intentar match por NOMBRE
                const nameMatch = findLocationByName(locationName, availableLocations);
                if (nameMatch) {
                    return {
                        success: true,
                        systemLocation: nameMatch,
                        matchType: 'NAME',
                        locationIQResult,
                        displayName: nameMatch.name,
                        isSystemLocation: true
                    };
                }

                // PASO 3: Intentar match por COORDENADAS
                const coordMatch = findLocationByCoordinatesLocal(latitude, longitude, availableLocations);
                if (coordMatch) {
                    return {
                        success: true,
                        systemLocation: coordMatch,
                        matchType: 'COORDINATES',
                        locationIQResult,
                        displayName: coordMatch.name,
                        isSystemLocation: true
                    };
                }

                // PASO 4: Usar LocationIQ como fallback
                return {
                    success: true,
                    systemLocation: null,
                    matchType: 'LOCATIONIQ_FALLBACK',
                    locationIQResult,
                    displayName: locationName,
                    isSystemLocation: false
                };
            } else {
                // LocationIQ falló, intentar solo por coordenadas
                const coordMatch = findLocationByCoordinatesLocal(latitude, longitude, availableLocations);

                if (coordMatch) {
                    return {
                        success: true,
                        systemLocation: coordMatch,
                        matchType: 'COORDINATES_ONLY',
                        locationIQResult: null,
                        displayName: coordMatch.name,
                        isSystemLocation: true
                    };
                }

                // Último fallback: usar coordenadas como string
                return {
                    success: true,
                    systemLocation: null,
                    matchType: 'COORDINATES_STRING',
                    locationIQResult: null,
                    displayName: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
                    isSystemLocation: false
                };
            }

        } catch (error) {
            console.error('❌ SmartLocationService - Error:', error);
            return {
                success: false,
                error: error.message,
                systemLocation: null,
                matchType: 'ERROR',
                locationIQResult: null,
                displayName: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
                isSystemLocation: false
            };
        }
    }

    // 🆕 Extraer nombre de ubicación priorizando solo 'town' o 'village'
    static extractLocationName(locationIQResult) {
        if (!locationIQResult) return null;

        // 1. Priorizar el campo 'town' si existe
        if (locationIQResult.address?.town) {
            return locationIQResult.address.town;
        }

        // 2. Solo village como fallback
        if (locationIQResult.address?.village) {
            return locationIQResult.address.village;
        }

        // 3. Si no hay town ni village, pero hay city, usarlo temporalmente para debug
        if (locationIQResult.city) {
            return locationIQResult.city;
        }

        // 4. Si no hay town ni village, usar display_name como último recurso
        if (locationIQResult.display_name) {
            return locationIQResult.display_name;
        }

        return 'Ubicación detectada';
    }

    // 🎯 Generar objeto SmartLocation para el backend
    static generateSmartLocation(detectionResult, coordinates) {
        const { systemLocation, matchType, displayName, isSystemLocation } = detectionResult;

        const smartLocation = {
            displayName: displayName,
            lastUpdated: Date.now()
        };

        // Agregar coordenadas si están disponibles
        if (coordinates) {
            smartLocation.coordinates = {
                lat: coordinates.latitude,
                lng: coordinates.longitude
            };
            smartLocation.isApproximate = coordinates.accuracy > 100;
        }

        // Determinar el source y serviceLocationId según el tipo de match
        switch (matchType) {
            case 'NAME':
            case 'COORDINATES':
            case 'COORDINATES_ONLY':
                smartLocation.source = "GPS";
                smartLocation.serviceLocationId = systemLocation.name;
                break;

            case 'LOCATIONIQ_FALLBACK':
            case 'COORDINATES_STRING':
                smartLocation.source = "GPS";
                // No agregar serviceLocationId - será ubicación personalizada
                break;

            default:
                smartLocation.source = "GPS";
                break;
        }

        return smartLocation;
    }

    // 🎯 Generar SmartLocation desde selección manual
    static generateManualSmartLocation(selectedLocation) {
        return {
            displayName: selectedLocation?.name || selectedLocation,
            source: "ZONE_SELECTION",
            serviceLocationId: selectedLocation?.name || selectedLocation,
            lastUpdated: Date.now()
        };
    }

    // 🎯 Generar SmartLocation desde input personalizado
    static generateCustomSmartLocation(customLocationText) {
        return {
            displayName: customLocationText,
            source: "CUSTOM_INPUT",
            lastUpdated: Date.now()
        };
    }

    // 🎯 Validar si una SmartLocation permite filtrar proveedores
    static canUseForFiltering(smartLocation) {
        return smartLocation?.serviceLocationId != null;
    }

    // 🎯 Obtener mensaje de estado para mostrar al usuario
    static getLocationStatusMessage(detectionResult) {
        const { matchType, isSystemLocation, displayName } = detectionResult;

        if (isSystemLocation) {
            return {
                type: 'success',
                icon: '✅',
                title: `Ubicación del sistema: ${displayName}`,
                description: 'Esta ubicación permite filtrar proveedores correctamente',
                canFilter: true
            };
        } else {
            return {
                type: 'warning',
                icon: '⚠️',
                title: `Ubicación personalizada: ${displayName}`,
                description: 'Esta ubicación puede limitar los resultados de búsqueda. Considera seleccionar una zona específica de la lista.',
                canFilter: false
            };
        }
    }
}

export default SmartLocationService;
